import React from 'react';
import { Shield, Lock, Eye, FileText, Mail, Phone } from 'lucide-react';
import { SEOSchema } from '../components/SEOSchema';
import { useLanguage } from '../contexts/LanguageContext';
export function PrivacyPolicyPage() {
  const {
    t
  } = useLanguage();
  return <div className="pt-20 min-h-screen bg-white" data-id="element-923">
      <SEOSchema type="about" title={t('Kebijakan Privasi | PT Graha Riau Gemilang', 'Privacy Policy | PT Graha Riau Gemilang')} description={t('Kebijakan privasi PT Graha Riau Gemilang mengenai pengumpulan, penggunaan, dan perlindungan data pribadi Anda.', 'PT Graha Riau Gemilang privacy policy regarding the collection, use, and protection of your personal data.')} breadcrumbs={[{
      name: t('Beranda', 'Home'),
      url: 'https://grahariaugemilang.com/'
    }, {
      name: t('Kebijakan Privasi', 'Privacy Policy'),
      url: 'https://grahariaugemilang.com/kebijakan-privasi'
    }]} data-id="element-924" />

      {/* Header */}
      <div className="bg-gradient-to-br from-gray-900 to-gray-800 text-white py-20" data-id="element-925">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-926">
          <div className="inline-block p-3 bg-white/10 rounded-full mb-6" data-id="element-927">
            <Shield className="w-8 h-8" data-id="element-928" />
          </div>
          <h1 className="text-4xl font-bold mb-4" data-id="element-929">
            {t('Kebijakan Privasi', 'Privacy Policy')}
          </h1>
          <p className="text-xl text-gray-300" data-id="element-930">
            {t('Komitmen kami dalam melindungi privasi dan data pribadi Anda', 'Our commitment to protecting your privacy and personal data')}
          </p>
          <p className="text-sm text-gray-400 mt-4" data-id="element-931">
            {t('Terakhir diperbarui: Desember 2024', 'Last updated: December 2024')}
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-id="element-932">
        <div className="prose prose-lg max-w-none" data-id="element-933">
          {/* Introduction */}
          <section className="mb-12" data-id="element-934">
            <div className="flex items-center mb-4" data-id="element-935">
              <FileText className="w-6 h-6 text-[#C4161C] mr-3" data-id="element-936" />
              <h2 className="text-2xl font-bold text-gray-900 m-0" data-id="element-937">
                {t('Pendahuluan', 'Introduction')}
              </h2>
            </div>
            <p className="text-gray-600 leading-relaxed" data-id="element-938">
              {t('PT Graha Riau Gemilang ("kami", "perusahaan") menghormati privasi Anda dan berkomitmen untuk melindungi data pribadi yang Anda berikan kepada kami. Kebijakan Privasi ini menjelaskan bagaimana kami mengumpulkan, menggunakan, menyimpan, dan melindungi informasi pribadi Anda ketika Anda menggunakan website kami atau berinteraksi dengan layanan kami.', 'PT Graha Riau Gemilang ("we", "company") respects your privacy and is committed to protecting the personal data you provide to us. This Privacy Policy explains how we collect, use, store, and protect your personal information when you use our website or interact with our services.')}
            </p>
          </section>

          {/* Information We Collect */}
          <section className="mb-12" data-id="element-939">
            <div className="flex items-center mb-4" data-id="element-940">
              <Eye className="w-6 h-6 text-[#C4161C] mr-3" data-id="element-941" />
              <h2 className="text-2xl font-bold text-gray-900 m-0" data-id="element-942">
                {t('Informasi yang Kami Kumpulkan', 'Information We Collect')}
              </h2>
            </div>
            <p className="text-gray-600 mb-4" data-id="element-943">
              {t('Kami dapat mengumpulkan informasi berikut:', 'We may collect the following information:')}
            </p>
            <ul className="space-y-2 text-gray-600" data-id="element-944">
              <li data-id="element-945">
                <strong data-id="element-946">
                  {t('Informasi Identitas:', 'Identity Information:')}
                </strong>{' '}
                {t('Nama lengkap, nomor identitas (KTP/SIM), tanggal lahir', 'Full name, ID number (KTP/SIM), date of birth')}
              </li>
              <li data-id="element-947">
                <strong data-id="element-948">
                  {t('Informasi Kontak:', 'Contact Information:')}
                </strong>{' '}
                {t('Alamat email, nomor telepon, alamat rumah/kantor', 'Email address, phone number, home/office address')}
              </li>
              <li data-id="element-949">
                <strong data-id="element-950">
                  {t('Informasi Finansial:', 'Financial Information:')}
                </strong>{' '}
                {t('Informasi pendapatan, riwayat kredit (jika diperlukan untuk pembiayaan)', 'Income information, credit history (if required for financing)')}
              </li>
              <li data-id="element-951">
                <strong data-id="element-952">
                  {t('Informasi Teknis:', 'Technical Information:')}
                </strong>{' '}
                {t('Alamat IP, jenis browser, sistem operasi, data cookies', 'IP address, browser type, operating system, cookie data')}
              </li>
              <li data-id="element-953">
                <strong data-id="element-954">
                  {t('Preferensi Properti:', 'Property Preferences:')}
                </strong>{' '}
                {t('Jenis properti yang diminati, lokasi pilihan, budget', 'Property type of interest, preferred location, budget')}
              </li>
            </ul>
          </section>

          {/* How We Use Information */}
          <section className="mb-12" data-id="element-955">
            <div className="flex items-center mb-4" data-id="element-956">
              <Lock className="w-6 h-6 text-[#C4161C] mr-3" data-id="element-957" />
              <h2 className="text-2xl font-bold text-gray-900 m-0" data-id="element-958">
                {t('Bagaimana Kami Menggunakan Informasi', 'How We Use Information')}
              </h2>
            </div>
            <p className="text-gray-600 mb-4" data-id="element-959">
              {t('Kami menggunakan informasi Anda untuk:', 'We use your information to:')}
            </p>
            <ul className="space-y-2 text-gray-600" data-id="element-960">
              <li data-id="element-961">
                {t('Memproses permintaan konsultasi dan pembelian properti', 'Process consultation requests and property purchases')}
              </li>
              <li data-id="element-962">
                {t('Memberikan informasi tentang proyek dan penawaran properti', 'Provide information about projects and property offers')}
              </li>
              <li data-id="element-963">
                {t('Memfasilitasi proses KPR dan pembiayaan properti', 'Facilitate mortgage and property financing processes')}
              </li>
              <li data-id="element-964">
                {t('Mengirimkan newsletter dan update marketing (dengan persetujuan Anda)', 'Send newsletters and marketing updates (with your consent)')}
              </li>
              <li data-id="element-965">
                {t('Meningkatkan layanan dan pengalaman pengguna website', 'Improve services and website user experience')}
              </li>
              <li data-id="element-966">
                {t('Mematuhi kewajiban hukum dan regulasi', 'Comply with legal and regulatory obligations')}
              </li>
            </ul>
          </section>

          {/* Data Protection */}
          <section className="mb-12" data-id="element-967">
            <div className="flex items-center mb-4" data-id="element-968">
              <Shield className="w-6 h-6 text-[#C4161C] mr-3" data-id="element-969" />
              <h2 className="text-2xl font-bold text-gray-900 m-0" data-id="element-970">
                {t('Perlindungan Data', 'Data Protection')}
              </h2>
            </div>
            <p className="text-gray-600 leading-relaxed" data-id="element-971">
              {t('Kami menerapkan langkah-langkah keamanan teknis dan organisasi yang sesuai untuk melindungi data pribadi Anda dari akses, penggunaan, atau pengungkapan yang tidak sah. Data Anda disimpan di server yang aman dan hanya dapat diakses oleh personel yang berwenang.', 'We implement appropriate technical and organizational security measures to protect your personal data from unauthorized access, use, or disclosure. Your data is stored on secure servers and can only be accessed by authorized personnel.')}
            </p>
          </section>

          {/* Cookies */}
          <section className="mb-12" data-id="element-972">
            <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-973">
              {t('Penggunaan Cookies', 'Use of Cookies')}
            </h3>
            <p className="text-gray-600 leading-relaxed" data-id="element-974">
              {t('Website kami menggunakan cookies untuk meningkatkan pengalaman browsing Anda. Cookies adalah file kecil yang disimpan di perangkat Anda. Anda dapat mengatur browser Anda untuk menolak cookies, namun ini mungkin mempengaruhi fungsionalitas website.', 'Our website uses cookies to enhance your browsing experience. Cookies are small files stored on your device. You can set your browser to refuse cookies, but this may affect website functionality.')}
            </p>
          </section>

          {/* Third Parties */}
          <section className="mb-12" data-id="element-975">
            <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-976">
              {t('Berbagi dengan Pihak Ketiga', 'Sharing with Third Parties')}
            </h3>
            <p className="text-gray-600 mb-4" data-id="element-977">
              {t('Kami dapat berbagi informasi Anda dengan:', 'We may share your information with:')}
            </p>
            <ul className="space-y-2 text-gray-600" data-id="element-978">
              <li data-id="element-979">
                {t('Bank dan lembaga keuangan untuk proses pembiayaan properti', 'Banks and financial institutions for property financing')}
              </li>
              <li data-id="element-980">
                {t('Notaris dan pihak legal untuk proses transaksi properti', 'Notaries and legal parties for property transactions')}
              </li>
              <li data-id="element-981">
                {t('Penyedia layanan teknologi dan marketing (dengan perjanjian kerahasiaan)', 'Technology and marketing service providers (with confidentiality agreements)')}
              </li>
              <li data-id="element-982">
                {t('Instansi pemerintah jika diwajibkan oleh hukum', 'Government agencies if required by law')}
              </li>
            </ul>
          </section>

          {/* User Rights */}
          <section className="mb-12" data-id="element-983">
            <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-984">
              {t('Hak Anda', 'Your Rights')}
            </h3>
            <p className="text-gray-600 mb-4" data-id="element-985">
              {t('Anda memiliki hak untuk:', 'You have the right to:')}
            </p>
            <ul className="space-y-2 text-gray-600" data-id="element-986">
              <li data-id="element-987">
                {t('Mengakses dan mendapatkan salinan data pribadi Anda', 'Access and obtain a copy of your personal data')}
              </li>
              <li data-id="element-988">
                {t('Meminta koreksi data yang tidak akurat', 'Request correction of inaccurate data')}
              </li>
              <li data-id="element-989">
                {t('Meminta penghapusan data pribadi Anda', 'Request deletion of your personal data')}
              </li>
              <li data-id="element-990">
                {t('Menolak atau membatasi pemrosesan data', 'Object to or restrict data processing')}
              </li>
              <li data-id="element-991">
                {t('Menarik persetujuan untuk komunikasi marketing', 'Withdraw consent for marketing communications')}
              </li>
            </ul>
          </section>

          {/* Contact */}
          <section className="mb-12" data-id="element-992">
            <div className="bg-gray-50 rounded-xl p-8 border border-gray-200" data-id="element-993">
              <h3 className="text-xl font-bold text-gray-900 mb-4" data-id="element-994">
                {t('Hubungi Kami', 'Contact Us')}
              </h3>
              <p className="text-gray-600 mb-4" data-id="element-995">
                {t('Jika Anda memiliki pertanyaan tentang Kebijakan Privasi ini atau ingin menggunakan hak Anda, silakan hubungi kami:', 'If you have questions about this Privacy Policy or wish to exercise your rights, please contact us:')}
              </p>
              <div className="space-y-3" data-id="element-996">
                <div className="flex items-center text-gray-700" data-id="element-997">
                  <Mail className="w-5 h-5 text-[#C4161C] mr-3" data-id="element-998" />
                  <a href="mailto:grahariaugemilang@gmail.com" className="hover:text-[#C4161C]" data-id="element-999">
                    grahariaugemilang@gmail.com
                  </a>
                </div>
                <div className="flex items-center text-gray-700" data-id="element-1000">
                  <Phone className="w-5 h-5 text-[#C4161C] mr-3" data-id="element-1001" />
                  <span data-id="element-1002">0761-21493 / 0761-47961</span>
                </div>
              </div>
            </div>
          </section>

          {/* Updates */}
          <section className="mb-12" data-id="element-1003">
            <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-1004">
              {t('Perubahan Kebijakan', 'Policy Changes')}
            </h3>
            <p className="text-gray-600 leading-relaxed" data-id="element-1005">
              {t('Kami dapat memperbarui Kebijakan Privasi ini dari waktu ke waktu. Perubahan akan dipublikasikan di halaman ini dengan tanggal "Terakhir diperbarui" yang baru. Kami mendorong Anda untuk meninjau kebijakan ini secara berkala.', 'We may update this Privacy Policy from time to time. Changes will be published on this page with a new "Last updated" date. We encourage you to review this policy periodically.')}
            </p>
          </section>
        </div>
      </div>
    </div>;
}